import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AppstoreOutlined, BankOutlined, LineChartOutlined, LogoutOutlined,
  MoonOutlined, SettingOutlined, SunOutlined, UnorderedListOutlined,
} from '@ant-design/icons'
import { useAuth } from '@argo/common/auth'
import { useTheme } from '@argo/common/theme'
import { useSession } from '@argo/common/session'
import { config } from '@argo/common/config'
import LanguageMenu from '@argo/common/components/LanguageMenu'

// `short` is what the bottom bar uses, where five labels share a phone width.
const tabs = [
  { to: '/', key: 'exchange', short: 'exchange', icon: <LineChartOutlined /> },
  { to: '/accounts', key: 'accounts', short: 'accounts', icon: <AppstoreOutlined /> },
  { to: '/ramp', key: 'ramp', short: 'tabRamp', icon: <BankOutlined /> },
  { to: '/events', key: 'events', short: 'events', icon: <UnorderedListOutlined /> },
  { to: '/settings', key: 'settings', short: 'settings', icon: <SettingOutlined /> },
]

export default function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { account, me } = useSession()
  const { resolved, setMode } = useTheme()
  const { signOut } = useAuth()
  const location = useLocation()

  // Keying the view on the route restarts its entrance animation once per
  // navigation, and never when data underneath it refreshes.
  const viewKey = location.pathname.split('/').slice(0, 2).join('/')

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">
          <span className="brand-mark" aria-hidden>A</span>
          <span className="only-wide">{t('app')}</span>
        </span>

        <nav className="topnav">
          {tabs.map(tab => (
            <NavLink key={tab.to} to={tab.to} end={tab.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
              {t(tab.key)}
            </NavLink>
          ))}
        </nav>

        <span className="topbar-spacer" />

        <div className="topbar-actions">
          {account && <span className="chip accent nowrap">{account}</span>}
          <LanguageMenu />
          <button
            className="icon-btn"
            onClick={() => setMode(resolved === 'dark' ? 'light' : 'dark')}
            aria-label={t('theme')}
            title={t('theme')}
          >
            {resolved === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          </button>
          {config.auth !== 'off' && (
            <button className="icon-btn" onClick={signOut} aria-label={t('signOut')} title={`${t('signOut')}${me ? ` · ${me.username}` : ''}`}>
              <LogoutOutlined />
            </button>
          )}
        </div>
      </header>

      <main className="main">
        <div className="view" key={viewKey}>{children}</div>
      </main>

      <nav className="tabbar">
        <div className="tabbar-inner">
          {tabs.map(tab => (
            <NavLink key={tab.to} to={tab.to} end={tab.to === '/'} className={({ isActive }) => (isActive ? 'active' : '')}>
              {tab.icon}
              <span>{t(tab.short)}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
